import { Component, ChangeDetectionStrategy } from '@angular/core';
import { DocumentRequirementsService } from './document-requirements.service';
import { BehaviorSubject, combineLatest, Subscription, Subject, debounceTime } from 'rxjs';
import DocType from '../../interfaces/doc-type';
import OwnedEntity from '../../interfaces/owned-entity';
import DocumentTypeRequirement from '../../interfaces/document-type-requirement';
import { Paginator, PaginatorInfo } from '../../types/paginator';
import { DataFormatService } from '../../services/data-format.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationModalComponent } from '../../components/confirmation-modal/confirmation-modal.component';
import { InitializeTrackerDialogComponent } from './initialize-tracker-dialog/initialize-tracker-dialog.component';
import {
  RequirementsFilter,
  UpsertDocRequirementInput,
} from './graphql/document-requirements';
import { CurrentUserService } from '../../services/current-user.service';
import { AddDocumentComponent } from './add-document/add-document.component';
import { FormControl, FormGroup } from '@angular/forms';
import ID from '../../types/id';
import Permission from '../../interfaces/permission';
import DocGroupLog from "../../interfaces/doc-group-log";
import { formatDate } from "@angular/common";

type PartialInput = {
  due_date?: string | null;
  requirement_flag?: boolean | null;
};

type DocumentTableItem = {
  owned_entity_name: string | null;
  related_name: string | null;
  related_url: string | null;
  closed_date: string | null;
  disposed_date: string | null;
  audit_requirement: null | number;
  return_requirement: null | number;
  audit_draft_requirement: DocumentTypeRequirement | null;
  audit_final_requirement: DocumentTypeRequirement | null;
  audit_permissions: Permission | null;
  return_draft_requirement: DocumentTypeRequirement | null;
  return_final_requirement: DocumentTypeRequirement | null;
  return_permissions: Permission | null;
  user_name: string | null;
  user_url: string | null;
  modified: string | null;
  entity: OwnedEntity;
};
@Component({
    selector: 'app-document-requirements',
    templateUrl: './document-requirements.component.html',
    styleUrls: ['./document-requirements.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DocumentRequirementsComponent {
  filterForm = new FormGroup({
    name: new FormControl<string | null>(null),
    tax_return_difficulty_level_id: new FormControl<ID | string | null>(null),
    include_all: new FormControl<boolean>(false),
    sort: new FormControl<string | null>(null),
  });
  private docTypes: DocType[];
  private _items: DocumentTableItem[] = [];
  private items = new BehaviorSubject(this._items);

  lastRefresh: DocGroupLog | null = null;

  public dueDateChange = new Subject<{type:string, subType:string, item: DocumentTableItem, event:Event}>();
  private dueDateChangeSub: Subscription;
  items$ = this.items.asObservable();
  reqSub: Subscription;
  columns = [
    'name',
    'closed_date',
    'disposed_date',
    'difficulty_level',
    'audit_requirement',
    'audit_dates',
    'return_requirement',
    'return_dates',
    'last_updated',
  ];
  public formSub: Subscription;
  constructor(
    public documentRequirementsService: DocumentRequirementsService,
    public dataFormat: DataFormatService,
    public dialog: MatDialog,
    public userService: CurrentUserService
  ) {}

  ngOnInit() {
    this.documentRequirementsService.getDocTypeRequirements();
    this.reqSub = combineLatest({
      docTypes: this.documentRequirementsService.docTypes$,
      docTypeRequirements:
        this.documentRequirementsService.docTypeRequirements$,
      massUpdateRuns: this.documentRequirementsService.massUpdateRuns$,
    }).subscribe(({ docTypes, docTypeRequirements, massUpdateRuns }) => {
      this.docTypes = docTypes;
      if(massUpdateRuns.length) {
        this.lastRefresh = massUpdateRuns[0];
      }
      this._processRequirements(docTypes, docTypeRequirements);
    });

    this.dueDateChangeSub = this.dueDateChange.pipe(debounceTime(500)).subscribe((change) => {
      this.updateDueDate(change.type, change.subType, change.item, change.event);
    });
    this.formSub = this.filterForm.valueChanges.subscribe(
      ({ name, tax_return_difficulty_level_id, include_all, sort }) => {
        {
          this.documentRequirementsService.updateEntityWhere(name ?? null);
          let filter: RequirementsFilter = {};
          if (tax_return_difficulty_level_id) {
            filter.tax_return_difficulty_level_id =
              tax_return_difficulty_level_id;
          }
          this.documentRequirementsService.setIncludeAll(!!include_all);
          this.documentRequirementsService.setOrder(sort ?? null);
          this.documentRequirementsService.setFilter(filter);
        }
      }
    );
  }
  private _processRequirements(
    docTypes: DocType[],
    requirements: Paginator<OwnedEntity, PaginatorInfo>
  ) {
    let items = [];
    for (let i = 0; i < requirements.data.length; i++) {
      let entity = requirements.data[i];
      let item: DocumentTableItem = {
        owned_entity_name: entity.name,
        related_name: entity.fund?.name ?? entity.project?.name ?? null,
        related_url: entity.fund
          ? this.dataFormat.formatLink(entity.fund)
          : entity.project
          ? this.dataFormat.formatLink(entity.project)
          : null,
        closed_date: null,
        disposed_date: null,
        audit_requirement: null,
        return_requirement: null,
        audit_draft_requirement: null,
        audit_final_requirement: null,
        audit_permissions:
          this.docTypes.find((docType) => docType.name === 'Audit')
            ?.permissions ?? null,
        return_draft_requirement: null,
        return_final_requirement: null,
        return_permissions:
          this.docTypes.find((docType) => docType.name === 'Tax Return')
            ?.permissions ?? null,
        user_name: null,
        user_url: null,
        modified: null,
        entity,
      };
      if (entity.fund?.close_date) {
        item.closed_date = entity.fund.close_date;
      }
      if (entity.project?.pipelineProjects) {
        for (let j = 0; j < entity.project.pipelineProjects.length; j++) {
          let pipelineProject = entity.project.pipelineProjects[j];
          for (
            let k = 0;
            k < pipelineProject.pipelineProjectDates.length;
            k++
          ) {
            let ppd = pipelineProject.pipelineProjectDates[k];
            if (ppd.pipelineMilestone?.milestone?.name === 'Closed') {
              item.closed_date = ppd.date ?? null;
            }
          }
        }
      }
      if (
        entity.project?.dispositions &&
        entity.project?.projectDetail?.fully_disposed
      ) {
        for (let j = 0; j < entity.project.dispositions.length; j++) {
          let disposition = entity.project.dispositions[j];
          if (disposition.dispositionPipelineDates) {
            for (
              let k = 0;
              k < disposition.dispositionPipelineDates.length;
              k++
            ) {
              let dpd = disposition.dispositionPipelineDates[k];
              if (dpd.pipelineMilestone?.milestone?.name === 'Closed') {
                item.disposed_date = dpd.date;
              }
            }
          }
        }
      }
      for (let j = 0; j < entity.documentTypeRequirements.length; j++) {
        let requirement = entity.documentTypeRequirements[j];
        let docType = docTypes.find(
          (iDocType) => iDocType.id === requirement.doc_type_id
        );
        if (!item.modified || requirement.modified > item.modified) {
          item.modified = requirement.modified;
          item.user_name = requirement.user?.person?.name ?? null;
          item.user_url = requirement.user?.person
            ? this.dataFormat.formatLink(requirement.user.person)
            : null;
        }
        if (docType) {
          let docSubtype = docType.docSubtypes.find(
            (iDocSubtype) => iDocSubtype.id === requirement.doc_subtype_id
          );
          if (docSubtype) {
            if (docType.name === 'Tax Return') {
              if (
                (requirement.requirement_flag === null ? -1 : (requirement.requirement_flag  ? 1 : 0)) >
                (item.return_requirement === null ? - 1 : (item.return_requirement ? 1:0))
              ) {
                item.return_requirement = requirement.requirement_flag as
                  | number
                  | null;
              }
              if (docSubtype.name === 'Draft') {
                item.return_draft_requirement = { ...requirement };
              } else if (docSubtype.name === 'Final') {
                item.return_final_requirement = { ...requirement };
              }
            } else if (docType.name === 'Audit') {
              if (
                (requirement.requirement_flag === null ? -1 : (requirement.requirement_flag ? 1 : 0)) >
                (item.audit_requirement === null ? -1 :( item.audit_requirement ? 1 : 0))
              ) {
                item.audit_requirement = requirement.requirement_flag as
                  | number
                  | null;
              }
              if (docSubtype.name === 'Draft') {
                item.audit_draft_requirement = { ...requirement };
              } else if (docSubtype.name === 'Final') {
                item.audit_final_requirement = { ...requirement };
              }
            }
          }
        }
      }
      items.push(item);
    }
    this._items = items;
    this.items.next(this._items);
  }
  refreshData() {
    let dialog = this.dialog.open(ConfirmationModalComponent, {
      data: {
        modalTitle: 'Refresh Document Requirement Data',
        actionMessage: 'This will update difficulty levels as appropriate and adjust document requirements as appropriate',
        warningMessage: this.lastRefresh ? `This process was last run by ${this.lastRefresh.user?.person?.name ?? 'System'} on ${formatDate(this.lastRefresh.created, 'MM/dd/yyyy','en-US')}` : '',
        modalInformation: [],
      },
    });
    dialog.afterClosed().subscribe((data) => {
      if (data?.clicked === 'Ok') {
        let sub: Subscription;
        sub = this.documentRequirementsService
          .refreshDocRequirements()
          .subscribe(() => {
            this.documentRequirementsService.refreshDocTypeRequirements();
            sub?.unsubscribe();
          });
      }
    });
  }
  applyFiltersOnEnterKey(event:KeyboardEvent) {
    if(event.code === 'Enter') {
      this.applyFilters();
    }
  }
  applyFilters() {
    this.documentRequirementsService.setPage(1);
    this.documentRequirementsService.refreshDocTypeRequirements();
  }
  clearFilters() {
    this.filterForm.reset();
    this.documentRequirementsService.setPage(1);
    this.documentRequirementsService.refreshDocTypeRequirements();
  }
  initializeTracker() {
    let dialog = this.dialog.open(InitializeTrackerDialogComponent, {});
    dialog.afterClosed().subscribe((data) => {});
  }
  addDocuments() {
    this.dialog.open(AddDocumentComponent);
  }
  updateDocRequirement(
    inputs: {
      type: string;
      subtype: string;
      item: DocumentTableItem;
      input: PartialInput;
    }[]
  ) {
    let sub: Subscription;
    let prepped: UpsertDocRequirementInput[] = [];
    sub = combineLatest({
      docTypes: this.documentRequirementsService.docTypes$,
      user: this.userService.user$,
    }).subscribe(({ docTypes, user }) => {
      for (let i = 0; i < inputs.length; i++) {
        let { type, subtype, item, input } = inputs[i];
        let docType = docTypes.find((iDocType) => iDocType.name === type);
        if (docType && user?.id) {
          let subType = docType.docSubtypes.find(
            (docSubtype) => docSubtype.name === subtype
          );
          if (subType) {
            prepped.push({
              doc_subtype_id: subType.id,
              doc_type_id: docType.id,
              owned_entity_id: item.entity.id,
              user_id: user.id,
              ...input,
            });
          }
        }
      }
      this.documentRequirementsService.upsertDocRequirements(prepped);
      sub?.unsubscribe();
    });
  }
  updateDueDate(
    type: string,
    subtype: string,
    item: DocumentTableItem,
    event: Event
  ) {
    let target = event.target as HTMLInputElement;
    let date = target.value;
    if (date.length === 10) {
      this.updateDocRequirement([
        { type, subtype, item, input: { due_date: date } },
      ]);
    } else if (date.length === 0) {
      this.updateDocRequirement([
        { type, subtype, item, input: { due_date: null } },
      ]);
    }
  }
  updateRequirementFlag(type: string, item: DocumentTableItem, required: any) {
    let sub: Subscription;
    let required_flag: boolean | null =
      required === 'null' ? null : required === 'true';
    this.updateDocRequirement([
      {
        type,
        subtype: 'Draft',
        item,
        input: {
          requirement_flag: required_flag,
        },
      },
      {
        type,
        subtype: 'Final',
        item,
        input: {
          requirement_flag: required_flag,
        },
      },
    ]);
  }
  updateDifficultyLevel(event: Event, item: DocumentTableItem) {
    let target = event.target as HTMLSelectElement;
    let difficulty = target.value === 'null' ? null : target.value;
    this.documentRequirementsService.updateDifficultyLevel(
      item.entity.id,
      difficulty
    );
  }
  getPage(page: number) {
    this.documentRequirementsService.setPage(page);
    this.documentRequirementsService.refreshDocTypeRequirements();
  }
  ngOnDestroy() {
    this.formSub?.unsubscribe();
    this.reqSub?.unsubscribe();
  }
}
